import { useState, useMemo } from "react";
import { Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  Button,
  TableSearch,
  Pagination,
  SearchFilterModal,
  type FilterCondition,
  type FilterableField,
} from "@/components/ui";
import { ExportOutlined } from "@ant-design/icons";

type Winner = {
  id: string;
  name: string;
  email: string;
  prize: string;
  category: string;
};

type Props = {
  winners: Winner[];
  eventSlug?: string;
};

const PAGE_SIZE = 50;

const CSV_COLUMNS: { header: string; key: keyof Winner }[] = [
  { header: "Attendee Name", key: "name" },
  { header: "Email", key: "email" },
  { header: "Prize", key: "prize" },
  { header: "Category", key: "category" },
];

// Wrap every value in quotes so commas, quotes and newlines survive the round
// trip into Excel / Sheets.
const escapeCsvValue = (value: string) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const WinnersList = ({ winners, eventSlug }: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);
  const [filterNameSearch, setFilterNameSearch] = useState("");

  // Build filter fields dynamically from unique prizes and categories
  const filterFields: FilterableField[] = useMemo(() => {
    const uniquePrizes = [...new Set(winners.map(w => w.prize))];
    const uniqueCategories = [...new Set(winners.map(w => w.category))];

    return [
      {
        id: 'prize',
        identifier: 'prize',
        label: 'Prize',
        type: 'select',
        options: uniquePrizes.map(p => ({ label: p, value: p })),
      },
      {
        id: 'category',
        identifier: 'category',
        label: 'Category',
        type: 'select',
        options: uniqueCategories.map(c => ({ label: c, value: c })),
      },
    ];
  }, [winners]);

  const filteredWinners = useMemo(() => {
    const normalizedSearch = appliedSearch.toLowerCase();
    return winners.filter((winner) => {
      // Search filter
      const matchesSearch = winner.name.toLowerCase().includes(normalizedSearch) ||
        winner.email.toLowerCase().includes(normalizedSearch);

      // Apply filter conditions
      const matchesConditions = filterConditions.every((condition) => {
        if (!condition.field || !condition.value) return true;
        const fieldValue = winner[condition.field as keyof Winner];
        if (condition.operator === 'is') {
          return fieldValue === condition.value;
        }
        if (condition.operator === 'contains') {
          return String(fieldValue).toLowerCase().includes(condition.value.toLowerCase());
        }
        if (condition.operator === 'not_equals') {
          return fieldValue !== condition.value;
        }
        return true;
      });

      return matchesSearch && matchesConditions;
    });
  }, [winners, appliedSearch, filterConditions]);

  const paginatedWinners = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredWinners.slice(startIndex, startIndex + pageSize);
  }, [filteredWinners, currentPage, pageSize]);

  const handleSearch = () => {
    setAppliedSearch(searchValue);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchValue("");
    setAppliedSearch("");
    setFilterConditions([]);
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    // Apply name search from filter modal
    if (filterNameSearch) {
      setAppliedSearch(filterNameSearch);
      setSearchValue(filterNameSearch);
    }
    setCurrentPage(1);
    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setFilterNameSearch("");
    setFilterConditions([]);
    setAppliedSearch("");
    setSearchValue("");
    setCurrentPage(1);
    setFilterModalOpen(false);
  };

  // Exports the winners currently in view (search + filters applied), matching
  // what the table shows rather than the unfiltered list.
  const handleExport = () => {
    if (filteredWinners.length === 0) {
      message.warning("No winners to export");
      return;
    }

    const rows = [
      CSV_COLUMNS.map((column) => escapeCsvValue(column.header)).join(","),
      ...filteredWinners.map((winner) =>
        CSV_COLUMNS.map((column) => escapeCsvValue(winner[column.key])).join(",")
      ),
    ];

    // Leading BOM so Excel picks up UTF-8 (names/emails may be non-ASCII).
    const blob = new Blob([`﻿${rows.join("\r\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");

    link.href = url;
    link.download = `winners_${eventSlug ? `${eventSlug}_` : ""}${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const columns: ColumnsType<Winner> = [
    {
      title: "Attendee Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Prize",
      dataIndex: "prize",
      key: "prize",
      sorter: (a, b) => a.prize.localeCompare(b.prize),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <TableSearch
          value={searchValue}
          onChange={setSearchValue}
          onSearch={handleSearch}
          onReset={handleReset}
          onFilter={() => setFilterModalOpen(true)}
          placeholder="Search Attendee Name"
          showFilter={true}
        />
        <Button
          variant="secondary"
          icon={<ExportOutlined />}
          onClick={handleExport}
        >
          Export Winners Report
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={paginatedWinners}
        rowKey="id"
        pagination={false}
        className="ava-data-table"
        size="middle"
        showSorterTooltip={false}
      />

      {filteredWinners.length > 0 && (
        <div className="mt-4">
          <Pagination
            current={currentPage}
            total={filteredWinners.length}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
      )}

      <SearchFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter Winners"
        description="Show winners that match the following..."
        fields={filterFields}
        filterConditions={filterConditions}
        onFilterConditionsChange={setFilterConditions}
        showNameSearch={true}
        nameSearchPlaceholder="Search by name or email"
        nameSearchValue={filterNameSearch}
        onNameSearchChange={setFilterNameSearch}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </div>
  );
};

export default WinnersList;
